import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
import { useAssignHunterToMerchantMutation } from "Api/Mutations/Merchants/useAssignHunterToMerchantMutation";
import { AssignHunterDialogActions } from "Components/Admin/UnassignedMerchants/AssignHunterDialogActions";
import { HunterAutocomplete } from "Components/Shared/Inputs/HunterAutocomplete";
import { HunterList } from "Components/Shared/Inputs/HunterList/HunterList";
import { LicenseShareCard } from "Components/Shared/Inputs/HunterList/LicenseShareCard";
import { HunterSingleSelectAutocomplete } from "Components/Shared/Inputs/HunterSingleSelectAutocomplete";
import type {
  HunterSearchDto,
  UnassignedMerchantListItemDto,
} from "Infrastructure/Api/Api";
import {
  type AssignHunterFormData,
  formatMainHunterCommission,
  getAssignHunterFormSchema,
} from "Schemas/assignHunterFormSchema";
import { useState } from "react";
import { type Path, useForm } from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Hunter.UnassignedMerchants.AssignDialog;

type LicensePublicID = string;

type Props = {
  merchant: UnassignedMerchantListItemDto;
  onClose: () => void;
};

export const AssignHunterForm = ({ merchant, onClose }: Props) => {
  // Form (RHF) holds what gets submitted; useState below holds full hunter DTOs
  // needed for rendering and the last-touched share license ID used to highlight
  // the over-limit error.
  const [primaryHunter, setPrimaryHunter] = useState<HunterSearchDto | null>(
    null,
  );
  const [shareHunters, setShareHunters] = useState<Array<HunterSearchDto>>([]);
  const [lastUpdatedShareLicensePublicID, setLastUpdatedShareLicensePublicID] =
    useState<LicensePublicID>("");
  const { t } = useResource();

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<AssignHunterFormData>({
    resolver: zodResolver(getAssignHunterFormSchema(t)),
    mode: "onChange",
    defaultValues: {
      primaryLicensePublicID: "",
      partyLicenseFees: {},
    },
  });

  // Fees are removed by rewriting the parent object, never by unregistering the nested field —
  // RHF's unregister prunes an emptied parent from the form values entirely, and the schema then
  // fails on partyLicenseFees being undefined, silently blocking every submit.
  const removePartyLicenseFee = (licensePublicID: string) => {
    const { [licensePublicID]: _, ...remainingFees } =
      watch("partyLicenseFees") ?? {};
    setValue("partyLicenseFees", remainingFees, { shouldValidate: true });
  };

  const formattedMainHunterCommission = formatMainHunterCommission(
    watch("partyLicenseFees"),
  );
  const hasShareError = !!errors.partyLicenseFees;

  const { mutate: assignHunter, isPending } = useAssignHunterToMerchantMutation(
    {
      onSuccess: () => {
        toast.success(t(RESOURCES.AssignSuccess));
        onClose();
      },
      onError: (error: unknown) => {
        const status = (error as { status?: number } | undefined)?.status;
        if (status === 409) {
          toast.error(t(RESOURCES.AlreadyHasHunterError));
          return;
        }
        toast.error(t(RESOURCES.GenericError));
      },
    },
  );

  const handlePrimaryChange = (newValue: HunterSearchDto | null) => {
    setPrimaryHunter(newValue);
    const newLicensePublicID = newValue?.licensePublicID ?? "";
    setValue("primaryLicensePublicID", newLicensePublicID, {
      shouldValidate: true,
    });
    setShareHunters((current) =>
      current.filter((h) => h.licensePublicID !== newLicensePublicID),
    );
    if (newLicensePublicID) {
      removePartyLicenseFee(newLicensePublicID);
    }
  };

  const handleShareHuntersChange = (hunters: Array<HunterSearchDto>) => {
    const filtered = hunters.filter(
      (h) =>
        !!h.licensePublicID &&
        h.licensePublicID !== primaryHunter?.licensePublicID,
    );
    setShareHunters(filtered);

    filtered.forEach(({ licensePublicID }) => {
      if (!licensePublicID) {
        return;
      }
      const currentValue = watch(
        `partyLicenseFees.${licensePublicID}` as Path<AssignHunterFormData>,
      );
      if (currentValue === undefined) {
        setValue(
          `partyLicenseFees.${licensePublicID}` as Path<AssignHunterFormData>,
          0,
          { shouldValidate: true },
        );
      }
    });
  };

  const handleRemoveShareHunter = (licensePublicID: string) => {
    setShareHunters((current) =>
      current.filter((h) => h.licensePublicID !== licensePublicID),
    );
    removePartyLicenseFee(licensePublicID);
  };

  const onSubmit = (data: AssignHunterFormData) => {
    if (!merchant.publicID) {
      return;
    }
    const partyLicenseFees = Object.fromEntries(
      Object.entries(data.partyLicenseFees).filter(([, value]) => value > 0),
    );
    assignHunter({
      merchantPublicID: merchant.publicID,
      request: {
        primaryLicensePublicID: data.primaryLicensePublicID,
        partyLicenseFees,
      },
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, () => {
        // A submit-time validation failure has no visible field to render on when no share
        // hunters are listed — never let it die silently again.
        toast.error(t(RESOURCES.GenericError));
      })}
    >
      <Stack gap={8}>
        <Stack gap={2}>
          <HunterSingleSelectAutocomplete
            selectedHunter={primaryHunter}
            onHunterChange={handlePrimaryChange}
            disabled={isPending}
            label={t(RESOURCES.PrimaryLicenseLabel)}
          />
          {primaryHunter && (
            <LicenseShareCard
              isPrimary
              hasError={hasShareError}
              companyName={t(RESOURCES.MainCommission)}
              licenseNo={primaryHunter.licenseNo ?? ""}
              commission={formattedMainHunterCommission}
            />
          )}
        </Stack>

        {primaryHunter && (
          <Stack gap={2}>
            <HunterAutocomplete
              selectedHunters={shareHunters}
              onHuntersChange={handleShareHuntersChange}
              disabled={isPending}
              label={t(RESOURCES.ShareLegend)}
            />
            <HunterList
              hunters={shareHunters}
              onRemoveHunter={handleRemoveShareHunter}
              control={control}
              hunterLicenseIDExceedingLimit={
                hasShareError ? lastUpdatedShareLicensePublicID : undefined
              }
              onCommissionUpdate={setLastUpdatedShareLicensePublicID}
            />
          </Stack>
        )}

        <AssignHunterDialogActions
          isPending={isPending}
          isSubmitDisabled={!isValid || isPending}
          onCancel={onClose}
        />
      </Stack>
    </form>
  );
};
